import { Clock, Users, Utensils, Receipt } from 'lucide-react';
import { forwardRef, memo } from 'react';
import { useI18n } from '../../i18n/I18nContext';

/**
 * Yeni "Dynamic Island" tarzı masa kartı.
 *
 * Mevcut props sözleşmesi korundu — Masalar.tsx içindeki mapTableToCard
 * fonksiyonu ile birebir uyumlu çalışır:
 *   table.number, table.status, table.total, table.guests, table.duration,
 *   table.section, table.waiter, table.bookTime, table.hasWarning,
 *   table.itemCount, table.orders (array, sadece sayım için kullanılır)
 */
/*
 * Status renkleri — bütün kartlar nötr gri border, parlayan/glow border yok.
 * DOLU kartta sadece sağ üst köşede yumuşak turuncu gradient (image 2 reference).
 */
const statusConfig: Record<
  'empty' | 'occupied' | 'reserved' | 'dirty',
  {
    dot: string;
    gradient: string;
    text: string;
    labelKey: string;
  }
> = {
  empty: {
    dot: 'bg-gray-300 dark:bg-gray-500',
    gradient: 'from-gray-200/40 dark:from-white/[0.06] to-transparent',
    text: 'text-gray-500 dark:text-gray-400',
    labelKey: 'table.status.empty',
  },
  occupied: {
    dot: 'bg-[#ff8a00]',
    // Tek katmanlı yumuşak turuncu — image 2 referansı
    gradient: 'from-[#ff8a00]/20 dark:from-[#ff8a00]/25 to-transparent',
    text: 'text-[#ff8a00]',
    labelKey: 'table.status.occupied',
  },
  reserved: {
    dot: 'bg-blue-500',
    gradient: 'from-blue-500/15 dark:from-blue-500/20 to-transparent',
    text: 'text-blue-600 dark:text-blue-400',
    labelKey: 'table.status.reserved',
  },
  dirty: {
    dot: 'bg-gray-500',
    gradient: 'from-gray-500/15 dark:from-gray-500/20 to-transparent',
    text: 'text-gray-600 dark:text-gray-400',
    labelKey: 'table.status.dirty',
  },
};

/*
 * NOT — performans için `motion.div` + `layout` + `whileHover` kaldırıldı.
 * 20+ kartın bulunduğu grid'de bu prop'lar her tema toggle / state
 * değişiminde tüm kartların yeniden layout/measure yapmasına neden oluyor.
 * Yerine CSS hover + `transform` + `React.memo` kullanıyoruz — native hız.
 */
export const TableCard = memo(
  forwardRef<HTMLDivElement, any>(function TableCard({ table, isSelected, onClick }, ref) {
    const { t } = useI18n();
    const status = (table?.status as keyof typeof statusConfig) || 'empty';
    const config = statusConfig[status] || statusConfig.empty;

    const itemCount =
      typeof table?.itemCount === 'number'
        ? table.itemCount
        : Array.isArray(table?.orders)
          ? table.orders.length
          : undefined;

    return (
      <div
        ref={ref}
        onClick={() => onClick && onClick(table)}
        className={`
          pos-card pos-press relative group cursor-pointer overflow-hidden min-h-[160px]
          rounded-2xl border
          bg-white dark:bg-[#161618]
          transition-[transform,border-color,box-shadow] duration-200 ease-out
          shadow-sm hover:-translate-y-1 hover:shadow-md
          ${
            isSelected
              ? 'border-[#ff8a00] dark:border-[#ff8a00] ring-1 ring-[#ff8a00]/60'
              : 'border-gray-200 dark:border-white/[0.06] hover:border-gray-300 dark:hover:border-white/20'
          }
        `}
      >
        {/* Köşe gradient — tek katman, image 2'deki gibi yumuşak */}
        <div
          className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${config.gradient} rounded-bl-full opacity-70 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}
        />

        <div className="p-5 relative z-10 flex flex-col h-full">
        {/* Header: numara + durum pill */}
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-3xl font-light tracking-tighter text-gray-900 dark:text-white">
            {table?.number}
          </h3>

          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-white dark:bg-black/50 border border-gray-100 dark:border-white/5 shadow-sm backdrop-blur-md">
            <span
              className={`w-1.5 h-1.5 rounded-full ${config.dot} ${
                status === 'occupied' ? 'animate-pulse shadow-[0_0_10px_rgba(255,138,0,0.8)]' : ''
              }`}
            />
            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              {t(config.labelKey)}
            </span>
          </div>
        </div>

        {/* İçerik */}
        <div className="flex-1 flex flex-col justify-end">
          {status === 'occupied' ? (
            <div className="space-y-3">
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">
                    {t("table.amount")}
                  </div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                    ₼{Number(table?.total || 0).toLocaleString('tr-TR')}
                  </div>
                </div>
                <div className="flex items-center gap-2.5 text-xs font-semibold text-[#ff8a00] bg-[#ff8a00]/10 px-2.5 py-1 rounded-lg">
                  {table?.guests ? (
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {table.guests}
                    </div>
                  ) : null}
                  {table?.guests && table?.duration ? (
                    <div className="w-px h-3 bg-[#ff8a00]/30" />
                  ) : null}
                  {table?.duration ? (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {table.duration}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="h-px w-full bg-gradient-to-r from-gray-200/0 via-gray-200 dark:via-white/10 to-gray-200/0" />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {table?.waiter ? (
                    <>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-white/10 dark:to-white/5 flex items-center justify-center text-[10px] font-semibold text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-white/5 shrink-0">
                        {String(table.waiter).charAt(0)}
                      </div>
                      <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400 truncate">
                        {table.waiter}
                      </span>
                    </>
                  ) : (
                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 truncate">
                      {table?.section}
                    </span>
                  )}
                </div>
                {typeof itemCount === 'number' && itemCount > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 shrink-0">
                    {t("table.orders", { n: itemCount })}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-2 opacity-40 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500">
              {status === 'empty' && <Utensils className={`w-5 h-5 ${config.text} mb-1.5`} />}
              {status === 'reserved' && <Users className={`w-5 h-5 ${config.text} mb-1.5`} />}
              {status === 'dirty' && <Receipt className={`w-5 h-5 ${config.text} mb-1.5`} />}
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 text-center">
                {status === 'reserved'
                  ? `${t("table.persons", { n: table?.guests || '' })}${table?.bookTime ? ` • ${table.bookTime}` : ''}`.trim()
                  : table?.section || ''}
              </span>
            </div>
          )}
        </div>

        {/* Süre aşımı uyarı bandı */}
        {table?.hasWarning && (
          <div className="absolute top-[-4px] left-1/2 -translate-x-1/2 px-3 py-1 bg-red-500 text-white text-[10px] font-bold rounded-b-xl shadow-lg shadow-red-500/20 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {t("table.overtime")}
          </div>
        )}
      </div>
    </div>
    );
  }),
  (prev, next) => {
    // Sadece değişen kart yeniden render olsun — grid genelinde yeniden render önlenir.
    if (prev.isSelected !== next.isSelected) return false;
    const a = prev.table, b = next.table;
    if (!a || !b) return a === b;
    return (
      a.id === b.id &&
      a.status === b.status &&
      a.total === b.total &&
      a.duration === b.duration &&
      a.guests === b.guests &&
      a.waiter === b.waiter &&
      a.itemCount === b.itemCount &&
      a.hasWarning === b.hasWarning &&
      a.section === b.section &&
      a.bookTime === b.bookTime &&
      a.number === b.number
    );
  },
);


